import Tone from 'tone';
import ModEdge from './ModEdge.js';
import PolyParameter from 'parameters/PolyParameter.js';

export default superclass => class extends superclass  {
    fm(carrier=this.carrier, reload=false, voice=this.voices[0], i=0) {
        voice.envelope.releaseCurve = 'end';
        voice.name = this.name;
        voice.isCarrier = !carrier;
        
        let isCarrier = carrier === undefined;

        // new frequency will be used by midi 
        if (isCarrier) {
            if (!voice._frequencyNode) {
                voice.frequency = new Tone.Signal(440, Tone.Type.Frequency);
                // voice.frequency.connect(voice.oscillator.frequency);
                voice._frequencyNode = voice.frequency;
            }
        }

        if (!voice.operatorFrequency) {
            voice.operatorFrequency = voice.oscillator.frequency;
        }
        
        if (!voice.harmonicity) {
            voice.harmonicity = new Tone.Multiply(0);
	    voice.harmonicity.units = Tone.Type.Positive;
            voice.octaves = new Tone.Multiply(0);
	    voice.octaves.units = Tone.Type.Positive;
            voice.modulationIndex = new Tone.Multiply(0);
	    voice.modulationIndex.units = Tone.Type.Positive;
            voice.feedback = new Tone.Gain(0);
        } else {
            voice.volume.value = 0;
            voice.octaves.output.gain.value = 0;
            voice.harmonicity.output.gain.value = 0;
            voice.modulationIndex.output.gain.value = 0;
            voice.feedback.gain.value = 0;
            voice.detune.value = 0;
        }
        
        if (isCarrier) {
            // this is the carrier oscillator
            voice.frequency.connect(voice.octaves);
            voice.octaves.connect(voice.harmonicity);
            //voice.frequency.connect(voice.modulationIndex);
        } else {
            // this is not a carrier oscillator-- it's a modulating oscillator
            carrier.voices[i].frequency.connect(voice.octaves);
            voice.octaves.connect(voice.harmonicity);
            carrier.voices[i].frequency.connect(voice.modulationIndex);
        }

        // finally connect the harmonicity to the actual oscillator's frequency 
        voice.harmonicity.connect(voice.oscillator.frequency);
        
        // cconnect the feedback
        voice.oscillator.frequency.connect(voice.feedback);

        if (this.feedback) {
            if (!this.feedbackEdge) {
                this.feedbackEdge  = new ModEdge(this, this.sequencerEngine, 0, "Feedback " + this.name);
            }
            this.connectFeedbackEdge(this.feedbackEdge, voice, i);
        }

        voice.disconnectFM = (deleteRouting) => {
            if (this.feedback) {
                this.feedbackEdge.disconnect(voice, i);
            }
            voice.oscillator.frequency.disconnect(voice.feedback);
            this.harmonicityParameter.removeAudioParam(voice.harmonicity.output.gain);
            this.oscillatorOctaveParameter.removeAudioParam(voice.octaves.output.gain);
            this.oscillatorLevelParameter.removeAudioParam(voice.volume);
            this.modulationIndexParameter.removeAudioParam(voice.modulationIndex.output.gain);
            this.feedbackParameter.removeAudioParam(voice.feedback.gain);
            this.detuneParameter.removeAudioParam(voice.detune);
            if (carrier) {
                // this is not a carrier, but a modulator osc
                carrier._voices[i].frequency.disconnect(voice.octaves);
                voice.octaves.disconnect(voice.harmonicity);
                carrier._voices[i].frequency.disconnect(voice.modulationIndex);
            } else {
                // this is the carrier osc
                voice.frequency.disconnect(voice.octaves);
                voice.octaves.disconnect(voice.harmonicity);
                //voice.frequency.disconnect(voice.modulationIndex);
            }
        };


        if (!reload) {
            // harmonicity
            this.harmonicityParameter = new PolyParameter(
                'Harmonicity', this.voices.map(v => v.harmonicity.output.gain),
                {
                    min: 0,
                    documentation: [
                        "Ratio between this operator`'s frequency, and the frequency of the inputed note.",
                        "Ex: A ratio of 2 is an octave above the inputed note"],
                    max: 20,
                    unit: '',
                    times: 2,
                    defaultValue: 3,
                },
                0
            );

            this.registerParameter(this.harmonicityParameter);

            this.oscillatorOctaveParameter = new PolyParameter(
                'octave', this.voices.map(v => v.octaves.output.gain),
                {
                    min: 0,
                    documentation: ["Controls octave"],
                    max: 8,
                    unit: '',
                    defaultValue: 1,
                    times: 2,
                },
                0
            );

            this.registerParameter(this.oscillatorOctaveParameter);
            
            // modulationIndex
            this.modulationIndexParameter= new PolyParameter(
                'modulationIndex', this.voices.map(v => v.modulationIndex.output.gain),
                {
                    documentation: "The level of modulation the operator will reach  at the maximum of it\'s envelope",
                    min: 0,
                    max: 120,
                    unit: '',
                    defaultValue: 1,
                });
            this.registerParameter(this.modulationIndexParameter);

            this.feedbackParameter = new PolyParameter(
                'feedback', this.voices.map(v => v.feedback.gain),
                {
                    documentation: "Controls the feedback,  bringing out the modulation for this operator.",
                    min: 0,
                    max: 10,
                    unit: '',
                    defaultValue: 0,
                    logDispose: true
                });
            this.registerParameter(this.feedbackParameter);
        } 

        if (reload) {
            this.harmonicityParameter.set(
                this.harmonicityParameter.get(true), true, true);
            this.oscillatorOctaveParameter.set(
                this.oscillatorOctaveParameter.get(true), true, true);
            this.modulationIndexParameter.set(
                this.modulationIndexParameter.get(true), true, true);
            this.feedbackParameter.set(
                this.feedbackParameter.get(true), true, true);
        }
    }

    setAudioParamsForParameters() {
        this.oscillatorLevelParameter.setAudioParams(
            this.voices.map(v => v.volume));
        this.detuneParameter.setAudioParams(
            this.voices.map(v => v.detune));
        this.harmonicityParameter.setAudioParams(
            this.voices.map(v => v.harmonicity.output.gain));
        this.oscillatorOctaveParameter.setAudioParams(
            this.voices.map(v => v.octaves.output.gain));
        this.modulationIndexParameter.setAudioParams(
            this.voices.map(v => v.modulationIndex.output.gain));
        this.feedbackParameter.setAudioParams(
            this.voices.map(v => v.feedback.gain));
    }

    connectFeedbackEdge(edge, modulator, i) {
        if (!edge) {
            this.feedbackEdge  = new ModEdge(this, this.sequencerEngine, 0, "Feedback " + this.name);
            edge = this.feedbackEdge;
        }
        if (!edge.modulatorNodes[i]) {
            edge.modulatorNodes.push(new Tone.Gain(0)); 
            edge.modulatorNodes[i].connect(modulator.oscillator.frequency);
        }
        modulator.connect(edge.modulatorNodes[i].gain);
        modulator.feedback.connect(edge.modulatorNodes[i]);

        edge.disconnect = (modulator, i) => {
            modulator.disconnect(edge.modulatorNodes[i].gain);
            modulator.feedback.disconnect(edge.modulatorNodes[i]);
        };
    }

    connectEdge(edge) {
        if (edge.operator.id === -2 || edge.operator.id === -3) {
            // we are connecting to either the synth outputs X or Y
            // via the edge. Remember, the edge we are given will
            // already be hooked up to the outputs upon construction
            // of the ModEdge object.
            // This just connects this oscillator to the edge
            let outputNode = edge.operator.id === -2 ?
                this.instrument.outputNodeX : this.instrument.outputNodeY;
            edge.disconnect = outputNode.connect(this);
            return edge;
        }

        let disconnects = [];
        // otherwise we are connecting this oscillator to another oscillator
        for (let i in this.voices) {
            let modulator = this.voices[i];

            // that means we need to hook up the modulator nodes, which are already
            // hooked up to the destination oscillators's audio nodes.
            modulator.connect(edge.modulatorNodes[i].gain);
            let opVoice = edge.operator.voices[i];
            modulator.modulationIndex.connect(edge.modulatorNodes[i]);

            disconnects.push(
                () => {
                    modulator.disconnect(edge.modulatorNodes[i].gain);
                    modulator.modulationIndex.disconnect(edge.modulatorNodes[i]);
                });
        }
        edge.disconnect = () =>
            disconnects.forEach(d => d());
        return edge;
    }
}
